import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { z } from 'zod';
import {
  DEFAULT_INVOICE_DESIGN,
  DEFAULT_ORDER_NUMBERING,
  DEFAULT_WATERMARK,
  EMPTY_BANK_SETTINGS,
  FORM_KEYS,
  formKeySchema,
  invoiceTemplateSchema,
  orderYearStyleSchema,
  resolveFormFields,
  watermarkPositionSchema,
  type FormKey,
  type InvoiceTemplate,
} from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import { buildUrl } from '../api/client.js';
import { getReadUrl } from '../media/media-api.js';

/**
 * The parts of the shop settings this tier's screens read and write.
 *
 * A NARROW SCHEMA, not the whole settings response, which is how the rest of this app reads
 * that endpoint (see `use-delivery-schedules.ts`). The response is fat by design — one call
 * draws a whole screen — and a client schema that insisted on every section would break the day
 * a section it does not care about gained a field.
 */

const bankSchema = z.object({
  accountName: z.string().nullable().default(null),
  accountNumber: z.string().nullable().default(null),
  ifsc: z.string().nullable().default(null),
  bankName: z.string().nullable().default(null),
  branch: z.string().nullable().default(null),
  upiId: z.string().nullable().default(null),
  showOnInvoice: z.boolean().default(false),
});

const orderNumberingSchema = z.object({
  prefix: z.string().default(DEFAULT_ORDER_NUMBERING.prefix),
  yearStyle: orderYearStyleSchema.default(DEFAULT_ORDER_NUMBERING.yearStyle),
  padding: z.number().int().default(DEFAULT_ORDER_NUMBERING.padding),
});

const watermarkSchema = z.object({
  enabled: z.boolean().default(false),
  text: z.string().nullable().default(null),
  position: watermarkPositionSchema.default(DEFAULT_WATERMARK.position),
  opacity: z.number().int().default(DEFAULT_WATERMARK.opacity),
});

const invoiceDesignSchema = z.object({
  template: invoiceTemplateSchema.default(DEFAULT_INVOICE_DESIGN.template),
  accentColor: z.string().default(DEFAULT_INVOICE_DESIGN.accentColor),
});

const formLayoutSchema = z.object({ form: formKeySchema, fields: z.array(z.string()) });

export const presentationSettingsSchema = z.object({
  shopName: z.string().default(''),
  bank: bankSchema.default(EMPTY_BANK_SETTINGS),
  orderNumbering: orderNumberingSchema.default(DEFAULT_ORDER_NUMBERING),
  watermark: watermarkSchema.default(DEFAULT_WATERMARK),
  invoiceDesign: invoiceDesignSchema.default(DEFAULT_INVOICE_DESIGN),
  formLayouts: z.array(formLayoutSchema).default([]),
});
export type PresentationSettings = z.infer<typeof presentationSettingsSchema>;

/**
 * Who the shop is and whether it charges GST — what a bill prints at the top.
 *
 * Every field optional and nullable for the same reason the presentation schema is narrow: a
 * shop that has never opened the section gets the API's empty defaults, and the screen must
 * render them as blank inputs rather than refuse to draw.
 */
const nullableText = z.string().nullable().optional();
export const shopDetailsSchema = z.object({
  shopName: z.string().default(''),
  legalName: nullableText,
  contact: z
    .object({
      primaryPhone: nullableText,
      alternatePhone: nullableText,
      whatsappPhone: nullableText,
      email: nullableText,
    })
    .partial()
    .default({}),
  address: z
    .object({
      line1: nullableText,
      line2: nullableText,
      city: nullableText,
      state: nullableText,
      stateCode: nullableText,
      pincode: nullableText,
    })
    .partial()
    .default({}),
  logo: z.object({ mediaId: nullableText }).partial().default({}),
  gst: z
    .object({
      registered: z.boolean().optional(),
      gstin: nullableText,
      defaultRatePercent: z.number().nullable().optional(),
      pricesIncludeTax: z.boolean().optional(),
    })
    .partial()
    .default({}),
});
export type ShopDetails = z.infer<typeof shopDetailsSchema>;

export const shopSettingsKeys = {
  presentation: () => ['settings', 'presentation'] as const,
  details: () => ['settings', 'details'] as const,
};

export function useShopDetails(): UseQueryResult<ShopDetails> {
  const client = useApiClient();
  return useQuery({
    queryKey: shopSettingsKeys.details(),
    queryFn: () => client.get('/settings', { schema: shopDetailsSchema }),
  });
}

export function usePresentationSettings(): UseQueryResult<PresentationSettings> {
  const client = useApiClient();
  return useQuery({
    queryKey: shopSettingsKeys.presentation(),
    queryFn: () => client.get('/settings', { schema: presentationSettingsSchema }),
  });
}

/**
 * A section at a time.
 *
 * `PATCH /settings` merges what it is sent and leaves the rest alone, which is what lets each
 * card on the screen save itself without carrying the other cards' values along — and without
 * one card's stale copy overwriting another card's fresh one.
 */
export function useUpdateShopSettings(): UseMutationResult<
  PresentationSettings,
  unknown,
  Record<string, unknown>
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      client.patch('/settings', patch, { schema: presentationSettingsSchema }),
    onSuccess: (settings) => {
      queryClient.setQueryData(shopSettingsKeys.presentation(), settings);
      // The watermark the uploader reads is a different, narrower query over the same endpoint.
      void queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });
}

const INVOICE_DESIGN_PREVIEW = '/settings/invoice-design/preview';

export interface InvoiceDesignPreview {
  pdf: Blob;
  /** The exact address the PDF was asked for — template and URL-encoded accent included. */
  requestUrl: string;
}

/**
 * A sample A4 bill in a design that has NOT been saved, as a PDF `Blob`.
 *
 * Fetched through the API client rather than set as an iframe `src`: auth is a short-lived
 * cookie that only this client knows how to refresh, and a frame pointed at the API directly
 * would show a 401's JSON the moment the access cookie lapsed. The caller turns the blob into an
 * object URL for the frame. Previous data is kept while the next design renders, so the frame
 * never blanks between two choices.
 */
export function useInvoiceDesignPreview(
  template: InvoiceTemplate,
  accent: string,
  enabled: boolean,
): UseQueryResult<InvoiceDesignPreview> {
  const client = useApiClient();
  return useQuery({
    queryKey: ['settings', 'invoice-design-preview', template, accent],
    enabled,
    queryFn: async ({ signal }) => {
      const query = { template, accent };
      return {
        pdf: await client.getBlob(INVOICE_DESIGN_PREVIEW, { query, signal }),
        requestUrl: buildUrl(client.baseUrl, INVOICE_DESIGN_PREVIEW, query),
      };
    },
    placeholderData: keepPreviousData,
  });
}

/** The fields one form shows, resolved — never `undefined`, so a form always renders. */
export function useFormFields(form: FormKey): string[] {
  const { data } = usePresentationSettings();
  const stored = data?.formLayouts.find((layout) => layout.form === form)?.fields;
  return stored ?? resolveFormFields(form, null);
}

/** Every form's layout, for the editor. */
export function useFormLayouts(): { form: FormKey; fields: string[] }[] {
  const { data } = usePresentationSettings();
  return FORM_KEYS.map((form) => ({
    form,
    fields:
      data?.formLayouts.find((layout) => layout.form === form)?.fields ??
      resolveFormFields(form, null),
  }));
}

/** A signed URL for the logo's thumbnail, so the settings card can show what is on the bill. */
export function useShopLogoUrl(mediaId: string | null | undefined): UseQueryResult<string> {
  const client = useApiClient();
  return useQuery({
    queryKey: ['settings', 'logo-url', mediaId],
    enabled: Boolean(mediaId),
    queryFn: async () => (await getReadUrl(client, mediaId as string, 'thumb')).url,
    // The URL is presigned and short-lived; a stale one is a broken image.
    staleTime: 60_000,
  });
}
